import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../styles/dashboard.module.css";

function BarChartSVG({ dashboardData, responsive = true, language }) {
  const { t, i18n } = useTranslation();
  const tFixed = language ? i18n.getFixedT(language) : t;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!dashboardData) {
    return (
      <div className={styles.divPibito}>
        <img className={styles.pibito} src="/img/Pibito.png" alt="No data" />
        <p className={styles.noData}>{tFixed("noData")}</p>
      </div>
    );
  }

  function textInsideBar(label, x0, x1, yCenter, barH) {
    const padding = 12;
    const tx = x0 + padding;
    const ty = yCenter + 7;

    if ((x1 - x0) < 80) return null;

    return (
      <text
        x={tx}
        y={ty}
        fill="#ffffff"
        fontSize={20}
        fontWeight="500"
      >
        {label}
      </text>
    );
  }

  const resolvedLanguage = (language || i18n.language || '').toLowerCase();
  const isEnglish = resolvedLanguage === 'en';
  const categories = [
    { key: "Goals", label: tFixed("Goals"), labelLines: isEnglish ? ["Purpose and", "Direction"] : ["Propósito y", "Dirección"] },
    { key: "Roles", label: tFixed("Roles"), labelLines: [tFixed("Roles")] },
    { key: "Processes", label: isEnglish ? "Processes" : "Procesos", labelLines: isEnglish ? ["Processes"] : ["Procesos"] },
    { key: "Interactions", label: tFixed("Interactions"), labelLines: isEnglish ? ["Interactions and", "Inclusion"] : ["Interacciones e", "Inclusión"] },
  ];

  const series = [
    { key: "team", avg: dashboardData.mediaEquipo || {}, std: dashboardData.desviacionEquipo || {}, color: "#d31273", label: tFixed("team") },
    { key: "personal", avg: dashboardData.mediaPersonal || {}, std: dashboardData.desviacionPersonal || {}, color: "#4a4a4f", label: tFixed("personal") },
  ];

  const referenceData = {
    benchmark: dashboardData.mediaEquipoGlobal,
    company: dashboardData.mediaEquipoEmpresa,
  };

  const data = categories.map(cat => ({
    name: cat.label,
    key: cat.key,
    labelLines: cat.labelLines,
    vals: series.map(s => {
      const avgValue = (s.avg && typeof s.avg === 'object') ? (s.avg[cat.key] ?? 0) : 0;
      const stdValue = (s.std && typeof s.std === 'object') ? (s.std[cat.key] ?? 0) : 0;
      return {
        label: s.label,
        avg: avgValue,
        std: stdValue,
        color: s.color,
      };
    }),
    benchmark: referenceData.benchmark?.[cat.key] ?? null,
    company: referenceData.company?.[cat.key] ?? null,
  }));

  const W = 1441;
  const H = 721;
  // Definir las 3 columnas: título, puntuación, barras
  const colTitleEnd = 200;      // Fin de la columna de títulos
  const colScoreStart = 220;    // Inicio de la columna de puntuaciones
  const colScoreEnd = 280;      // Fin de la columna de puntuaciones
  const colBarsStart = 290;     // Inicio de la columna de barras (nuevo m.left efectivo)

  // Márgenes reducidos para eliminar espacios vacíos
  const m = { top: 40, right: 160, bottom: 40, left: colBarsStart };
  const chartW = W - m.left - m.right;
  const chartH = H - m.top - m.bottom;

  const maxVal = 4;
  const xScale = v => m.left + (v / maxVal) * chartW;

  const groupCount = data.length;
  const rowH = chartH / groupCount;
  const barGapFrac = 0.3;
  const barsAreaH = rowH * (1 - barGapFrac);
  const barGap = 6;
  const barH = (barsAreaH - barGap) / 2;

  const xTicksVals = [0, 1, 2, 3];
  const xTicks = xTicksVals.map(v => {
    const x = xScale(v);
    return (
      <g key={v}>
        <line
          x1={x}
          y1={m.top}
          x2={x}
          y2={H - m.bottom}
          stroke="#a3a3a3"
          strokeDasharray="4 4"
        />
        <text
          x={x}
          y={m.top - 5}
          textAnchor="middle"
          style={{ fontSize: 20, fill: "#595959" }}
        >
          {v}
        </text>
      </g>
    );
  });

  const x4 = xScale(4);
  const xEndAxis = (
    <g>
      <line
        x1={x4}
        y1={m.top}
        x2={x4}
        y2={H - m.bottom}
        stroke="#a3a3a3"
        strokeDasharray="4 4"
      />
      <text
        x={x4}
        y={m.top - 5}
        textAnchor="middle"
        style={{ fontSize: 20, fill: "#595959" }}
      >
        4
      </text>
    </g>
  );

  const groups = data.flatMap((grp, i) => {
    const rowTop = m.top + i * rowH;
    const rowCenter = rowTop + rowH / 2;

    const teamYCenter = rowCenter - (barH / 2 + barGap / 2);
    const personalYCenter = rowCenter + (barH / 2 + barGap / 2);

    // Y donde está el techo de la barra rosa
    const yTopTeam = teamYCenter - barH / 2;

    // Punta del triángulo ▼ tocando la barra (ajustado para márgenes reducidos)
    const tipY = yTopTeam - 15; // reducido para márgenes más pequeños

    // Tamaño triángulo
    const triW = 25;
    const triH = 17;

    // separación horizontal
    const offset = 22;

    const elems = [];

    // 🔶 TRIÁNGULO AMARILLO ▼ (company)
    if (grp.company != null) {
      const xC = xScale(grp.company);
      elems.push(
        <polygon
          key={`company-${i}`}
          points={`
            ${xC},${tipY + triH} 
            ${xC - triW / 2},${tipY}
            ${xC + triW / 2},${tipY}
          `}
          fill="#FFD700"
        />
      );
    }

    // 🔷 TRIÁNGULO AZUL ▼ (benchmark)
    if (grp.benchmark != null) {
      const xB = xScale(grp.benchmark) + offset;
      elems.push(
        <polygon
          key={`bench-${i}`}
          points={`
            ${xB},${tipY + triH}
            ${xB - triW / 2},${tipY}
            ${xB + triW / 2},${tipY}
          `}
          fill="#87CEEB"
        />
      );
    }

    // ======== BARRAS =========
    const barPositions = [
      { index: 0, yCenter: teamYCenter },
      { index: 1, yCenter: personalYCenter },
    ];

    barPositions.forEach(({ index, yCenter }) => {
      const v = grp.vals[index];
      const avg = Math.min(4, Math.max(0, v.avg));
      const std = v.std ?? 0;

      const x0 = xScale(0);
      const xAvg = xScale(avg);

      const yTop = yCenter - barH / 2;
      const yBottom = yCenter + barH / 2;
      const radius = 6;

      const d = [
        `M ${x0},${yBottom}`,
        `L ${x0},${yTop}`,
        `L ${xAvg - radius},${yTop}`,
        `A ${radius},${radius} 0 0 1 ${xAvg},${yTop + radius}`,
        `L ${xAvg},${yBottom}`,
        `Z`,
      ].join(" ");

      elems.push(
        <g key={`bar-${i}-${index}`}>
          <path d={d} fill={v.color} />
          {textInsideBar(v.label, x0, xAvg, yCenter, barH)}

          {std > 0 && (
            <g stroke="#000" strokeWidth="2">
              {(() => {
                const xMin = xScale(Math.max(0, avg - std));
                const xMax = xScale(Math.min(4, avg + std));
                return (
                  <>
                    <line x1={xMin} y1={yCenter} x2={xMax} y2={yCenter} />
                    <line x1={xMin} y1={yCenter - 6} x2={xMin} y2={yCenter + 6} />
                    <line x1={xMax} y1={yCenter - 6} x2={xMax} y2={yCenter + 6} />
                  </>
                );
              })()}
            </g>
          )}

          {/* Puntuación en columna 2 (centro) */}
          <text
            x={(colScoreStart + colScoreEnd) / 2}
            y={yCenter + 6}
            textAnchor="middle"
            fill="#595959"
            fontSize={20}
            fontWeight="bold"
          >
            {avg.toFixed(2)}
          </text>
        </g>
      );
    });

    // Etiqueta de categoría (con soporte para múltiples líneas)
    const labelLines = grp.labelLines || [grp.name];
    if (labelLines.length === 1) {
      elems.push(
        <text
          key={`label-${i}`}
          x={colTitleEnd - 20}
          y={rowCenter + 6}
          textAnchor="end"
          style={{ fontSize: 22, fill: "#595959" }}
        >
          {labelLines[0]}
        </text>
      );
    } else {
      // Múltiples líneas
      labelLines.forEach((line, lineIndex) => {
        const lineY = rowCenter - (labelLines.length - 1) * 12 + lineIndex * 24;
        elems.push(
          <text
            key={`label-${i}-${lineIndex}`}
            x={colTitleEnd - 20}
            y={lineY}
            textAnchor="end"
            style={{ fontSize: 22, fill: "#595959" }}
          >
            {line}
          </text>
        );
      });
    }

    return elems;
  });

  const svgElement = (
    <svg
      width={W}
      height={H}
      viewBox={responsive ? `0 0 ${W} ${H}` : undefined}
      style={responsive ? { width: "100%", height: "100%", maxHeight: "100%", objectFit: "contain" } : {}}
      preserveAspectRatio={responsive ? "xMidYMid meet" : undefined}
    >
      {xTicks}
      {xEndAxis}
      {groups}
    </svg>
  );

  if (responsive) {
    return (
      <div style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "0" : "1.5rem",
        padding: isMobile ? "0.5rem" : "1rem",
        overflow: "hidden",
        alignItems: "flex-start"
      }}>
        {/* Gráfico */}
        <div style={{
          flex: isMobile ? "1 1 100%" : "0 1 65%",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "stretch",
          minWidth: 0,
          maxWidth: isMobile ? "100%" : "65%",
          width: isMobile ? "100%" : "auto",
          height: "100%"
        }}>
          <div style={{
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "stretch"
          }}>
            {svgElement}
          </div>
        </div>

        {/* Leyenda a la derecha - oculta en móvil */}
        {!isMobile && (
          <div style={{
            flex: "0 0 auto",
            width: "300px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingLeft: "0.5rem",
            height: "100%"
          }}>
            <ChartLegend />
          </div>
        )}
      </div>
    );
  }

  return svgElement;
}

function ChartLegend() {
  const { t } = useTranslation();

  return (
    <div style={{
      backgroundColor: "#fff",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start"
    }}>
      <p style={{
        margin: "0 0 0.5rem 0",
        fontSize: "0.75rem",
        color: "#000",
        lineHeight: "1.6",
        textAlign: "left"
      }}>
        {t("chartLegend.frequencyScale")}
      </p>
      <div style={{
        marginBottom: "0.75rem",
        fontSize: "0.75rem",
        color: "#000",
        lineHeight: "1.8"
      }}>
        <div>{t("chartLegend.never")}</div>
        <div>{t("chartLegend.rarely")}</div>
        <div>{t("chartLegend.sometimes")}</div>
        <div>{t("chartLegend.often")}</div>
        <div>{t("chartLegend.always")}</div>
      </div>

      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "0.75rem"
      }}>
        <svg width="20" height="14" style={{ flexShrink: 0 }}>
          <polygon points="10,14 0,0 20,0" fill="#87CEEB" />
        </svg>
        <span style={{
          fontSize: "0.8rem",
          color: "#000",
          lineHeight: "1.4"
        }}>
          {t("chartLegend.referenceSampleMean")}
        </span>
      </div>

      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "0.75rem"
      }}>
        <svg width="20" height="14" style={{ flexShrink: 0 }}>
          <polygon points="10,14 0,0 20,0" fill="#FFD700" />
        </svg>
        <span style={{
          fontSize: "0.8rem",
          color: "#000",
          lineHeight: "1.4"
        }}>
          {t("chartLegend.organizationMean")}
        </span>
      </div>

      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem"
      }}>
        <span style={{
          fontSize: "1rem",
          lineHeight: "1",
          color: "#000",
          fontFamily: "'Benton Sans', 'Avenir', sans-serif",
          fontWeight: "600"
        }}>
          {t("chartLegend.h")}
        </span>
        <span style={{
          fontSize: "0.8rem",
          color: "#000",
          lineHeight: "1.4"
        }}>
          {t("chartLegend.standardDeviation")}
        </span>
      </div>
    </div>
  );
}

export default BarChartSVG;
